import axios from "axios";
import { getCurrentTenant } from "./tenantConfig";
import { refreshAccessToken } from "./tokenRefreshService";

const {
  apiBaseUrl: API_BASE_URL,
  API_USERS_HOST,
  projectCode: TENANT_PROJECT_CODE,
} = getCurrentTenant();

const isPersonalMinusOneResponse = (data) =>
  TENANT_PROJECT_CODE === "PERSONAL" && Number(data?.returnCode) === -1;

// Axios instance oluştur
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  testURL: API_USERS_HOST,
  timeout: 120000,
  headers: {
    "Content-Type": "application/json",
  },
});

// 401 bekleyen istekler kuyruğu
let waitingQueue = [];

function processQueue(newToken) {
  waitingQueue.forEach(({ resolve, reject }) => {
    if (newToken) {
      resolve(newToken);
    } else {
      reject(new Error("Token refresh failed"));
    }
  });
  waitingQueue = [];
}

// Kullanıcıyı login'e yönlendir
function forceLogout() {
  localStorage.clear();
  window.location.href = "/login";
}

// Request Interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const projectCode = localStorage.getItem("projectCode")?.trim();
    const authToken = localStorage.getItem("authToken");

    // Project code'u URL'e ekle.
    // NOT: "zaten var mı?" kontrolü ilk path segmentine bakar. Düz `includes`
    // kullanılırsa, serial içinde project code geçen (örn. "RCU010000002")
    // isteklerde yanlış pozitif olur ve `/RCU` öneki eklenmezdi.
    if (
      projectCode &&
      config.url &&
      !config.url.startsWith(`/${projectCode}/`) &&
      config.url !== `/${projectCode}`
    ) {
      config.url = `/${projectCode}${config.url}`;
    }

    // Auth token'ı header'a ekle
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }

    // Request'i logla (development için)
    if (process.env.NODE_ENV === "development") {
      console.log("🚀 Request:", config.method.toUpperCase(), config.url);
    }

    return config;
  },
  (error) => {
    console.error("❌ Request Error:", error);
    return Promise.reject(error);
  },
);

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Response'u logla (development için)
    if (process.env.NODE_ENV === "development") {
      console.log("✅ Response:", response.config.url, response.status);
    }

    // API'niz returnCode kullanıyorsa, burada kontrol et
    if (response.data && response.data.returnCode !== undefined) {
      if (response.data.returnCode === 0) {
        // Başarılı yanıt - sadece data'yı döndür
        return response.data.data;
      } else {
        const exMessage = response.data.exMessage || "";

        // Backend HTTP 200 döndü ama auth hatası varsa → logout
        const authKeywords = [
          "no auth",
          "noauth",
          "unauthorized",
          "token",
          "auth fail",
          "authentication",
          "yetkisiz",
          "oturum",
        ];
        const isAuthError = authKeywords.some((kw) =>
          exMessage.toLowerCase().includes(kw),
        );

        if (isAuthError && !isPersonalMinusOneResponse(response.data)) {
          console.log(
            "[AxiosInterceptor] Auth error in returnCode response:",
            exMessage,
          );
          forceLogout();
          return Promise.reject(new Error(exMessage));
        }

        if (isPersonalMinusOneResponse(response.data)) {
          console.warn(
            "[AxiosInterceptor] PERSONAL returnCode -1: logout uygulanmadı.",
            {
              url: response.config?.url,
              exMessage,
            },
          );
        }

        // API'den gelen normal hata
        const error = new Error(exMessage || "İşlem başarısız oldu");
        error.returnCode = response.data.returnCode;
        error.response = response;
        throw error;
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // PERSONAL servislerinde returnCode -1 kullanıcı oturumunu sonlandırmamalı.
    // HTTP status 401/403 olsa bile hata çağıran bileşene iletilir.
    if (isPersonalMinusOneResponse(error.response?.data)) {
      console.warn(
        "[AxiosInterceptor] PERSONAL HTTP hata yanıtında returnCode -1: logout uygulanmadı.",
        {
          url: originalRequest?.url,
          status,
          exMessage: error.response?.data?.exMessage || "",
        },
      );
      return Promise.reject(error);
    }

    // 401 veya 403 — Token süresi dolmuş veya yetki hatası
    if ((status === 401 || status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;

      // Merkezi refreshAccessToken zaten mutex yönetiyor.
      // İlk çağıran refresh'i başlatır, diğerleri aynı promise'i bekler.
      try {
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Kuyrukta bekleyen istekleri de çöz
          processQueue(newToken);

          // Orijinal isteği yeni token ile tekrarla
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        }

        // Token yenilenemedi → logout
        processQueue(null);
        forceLogout();
        return Promise.reject(error);
      } catch (refreshError) {
        processQueue(null);
        forceLogout();
        return Promise.reject(refreshError);
      }
    }

    // 404 Not Found
    if (status === 404) {
      console.error("🔍 Kaynak bulunamadı:", error.config.url);
    }

    // 500 Server Error
    if (status >= 500) {
      console.error("🔥 Sunucu hatası:", status);
    }

    // Network Error
    if (!error.response) {
      console.error("📡 Ağ hatası veya sunucuya erişilemiyor");
    }

    console.error("❌ Response Error:", error.message);
    return Promise.reject(error);
  },
);

export default axiosInstance;
